import { isApiError } from "@pokifolio/api-client";
import {
  PRICE_FRESH_HOURS,
  type PriceConfirmation,
  PriceInputSchema,
  type ResearchRecord,
  isPriceFresh,
} from "@pokifolio/contracts";
import { ExternalLink } from "lucide-react";
import { type SubmitEvent, useId, useState } from "react";

import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Field } from "../../components/ui/field";
import { Input, Select } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { useCurrentUser } from "../auth/session";
import { formatDateTime, formatMoney } from "../search/format";
import { editRule } from "./edit-rule";
import { Fact } from "./fact";
import { formatDate } from "./format";
import { useConfirmPrice } from "./research-queries";

const COMMON_CURRENCIES = ["USD", "GBP", "EUR", "CAD", "AUD", "JPY", "PKR"] as const;

/** eBay listings get their price from eBay itself (D-54); a web page's copy of an eBay item may be typed too. */
const isOnEbay = (research: ResearchRecord) =>
  research.listing.source === "ebay" || research.listing.website === "eBay";

/** The price check on a research record: the price now, plus shipping, in US dollars at today's rate. */
export function PriceCheck({ research }: { research: ResearchRecord }) {
  const me = useCurrentUser();
  const rule = editRule(research, me, "the price check");
  const [editing, setEditing] = useState(false);
  const confirm = useConfirmPrice(research.id);
  const headingId = useId();
  const { price } = research;

  const startEditing = () => {
    confirm.reset();
    setEditing(true);
  };

  return (
    <Card>
      <section aria-labelledby={headingId} className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id={headingId} className="text-base font-semibold text-foreground">
            Price check
          </h2>
          {price !== null && rule.allowed && !editing && (
            <Button variant="secondary" size="sm" onClick={startEditing}>
              Check the price again
            </Button>
          )}
        </div>

        {!editing && confirm.isError && <ErrorAlert error={confirm.error} />}

        {editing ? (
          <PriceForm
            research={research}
            confirm={confirm}
            onDone={() => setEditing(false)}
            onCancel={() => {
              confirm.reset();
              setEditing(false);
            }}
          />
        ) : price === null ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">Not checked yet.</p>
            {rule.allowed && <Button onClick={startEditing}>Check the price</Button>}
          </div>
        ) : (
          <PriceSummary price={price} research={research} currentUserId={me.user.id} />
        )}

        {!rule.allowed && rule.reason !== null && (
          <p className="text-xs text-muted-foreground">{rule.reason}</p>
        )}
      </section>
    </Card>
  );
}

interface PriceFormProps {
  readonly research: ResearchRecord;
  readonly confirm: ReturnType<typeof useConfirmPrice>;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

function PriceForm({ research, confirm, onDone, onCancel }: PriceFormProps) {
  const { listing } = research;
  const onEbay = isOnEbay(research);
  // A web page's copy of an eBay item can still be typed; eBay's own data never is.
  const canType = listing.source !== "ebay";
  const [typing, setTyping] = useState(!onEbay);
  const [amount, setAmount] = useState(listing.price?.amount ?? "");
  const [currency, setCurrency] = useState(listing.price?.currency ?? "USD");
  const [shipping, setShipping] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const ids = { amount: useId(), currency: useId(), shipping: useId() };
  const currencies: readonly string[] = (COMMON_CURRENCIES as readonly string[]).includes(currency)
    ? COMMON_CURRENCIES
    : [...COMMON_CURRENCIES, currency];

  const errorFor = (key: string): string | undefined =>
    errors[key] ?? serverFieldError(confirm.error, `price.${key}`);
  const fieldErrorShown =
    Object.keys(errors).length > 0 ||
    (isApiError(confirm.error) && confirm.error.fieldErrors.some((e) => e.path.startsWith("price.")));

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const input = typing
      ? { source: "employee", amount, currency, shippingAmount: shipping }
      : { source: "ebay", shippingAmount: shipping };
    const result = validate(PriceInputSchema, input);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    confirm.mutate(
      { price: result.data, version: research.version },
      {
        onSuccess: onDone,
        // Refused (sold out, ended, or changed by someone else): close and show why, with the latest record.
        onError: (error) => {
          if (isApiError(error) && error.status === 409) onDone();
        },
      },
    );
  }

  const listingLink = (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
    >
      Open the listing
      <ExternalLink className="size-3.5" aria-hidden="true" />
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {typing ? (
        <p className="text-sm text-muted-foreground">
          Copy the price and the shipping cost to us from the website. {listingLink}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Pokifolio asks eBay for the price right now. Type the shipping cost to us from the listing page (0
          if shipping is free). {listingLink}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        {typing && (
          <>
            <Field id={ids.amount} label="Price" error={errorFor("amount")}>
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field id={ids.currency} label="Currency" error={errorFor("currency")}>
              <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Field
          id={ids.shipping}
          label={typing ? "Shipping cost" : "Shipping cost (in the listing's currency)"}
          error={errorFor("shippingAmount")}
        >
          <Input inputMode="decimal" value={shipping} onChange={(e) => setShipping(e.target.value)} />
        </Field>
      </div>
      {errorFor("source") !== undefined && <p className="text-xs text-destructive">{errorFor("source")}</p>}

      {confirm.isError && !fieldErrorShown && <ErrorAlert error={confirm.error} />}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={confirm.isPending}>
          {confirm.isPending ? "Checking…" : typing ? "Save price" : "Check price on eBay"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={confirm.isPending}>
          Cancel
        </Button>
        {onEbay && canType && (
          <Button
            variant="ghost"
            onClick={() => {
              setErrors({});
              confirm.reset();
              setTyping(!typing);
            }}
            disabled={confirm.isPending}
          >
            {typing ? "Ask eBay instead" : "Type the price instead"}
          </Button>
        )}
      </div>
    </form>
  );
}

interface PriceSummaryProps {
  readonly price: PriceConfirmation;
  readonly research: ResearchRecord;
  readonly currentUserId: string;
}

function PriceSummary({ price, research, currentUserId }: PriceSummaryProps) {
  const inUsd = price.currency === "USD";
  const auction = research.listing.listingType === "auction" && price.source === "ebay";
  const total = (Number(price.amount) + Number(price.shippingAmount)).toFixed(2);
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <Fact label={auction ? "Current bid" : "Price"}>
          {formatMoney({ amount: price.amount, currency: price.currency })}
        </Fact>
        <Fact label="Shipping">
          {formatMoney({ amount: price.shippingAmount, currency: price.currency })}
        </Fact>
        <Fact label={inUsd ? "Total" : "Total in US dollars"}>
          <span className="font-semibold">{formatMoney({ amount: price.totalUsd, currency: "USD" })}</span>
          {!inUsd && price.fxRate !== null && price.fxDate !== null && (
            <span className="block text-xs text-muted-foreground">
              {formatMoney({ amount: total, currency: price.currency })} at 1 USD = {price.fxRate}{" "}
              {price.currency} ({formatDate(`${price.fxDate}T12:00:00.000Z`)})
            </span>
          )}
        </Fact>
      </dl>
      <p className="text-xs text-muted-foreground">
        {price.source === "ebay" ? "From eBay" : "Typed from the website"} · checked by{" "}
        {price.confirmedBy.id === currentUserId ? "you" : price.confirmedBy.name} ·{" "}
        {formatDateTime(price.confirmedAt)}
      </p>
      {!isPriceFresh(price.confirmedAt) && (
        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
          ⚠ Checked more than {PRICE_FRESH_HOURS} hours ago. Prices change: check again before asking for
          approval.
        </p>
      )}
    </div>
  );
}
